import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { 
  Users, 
  CheckCircle2, 
  XCircle, 
  TrendingUp,
  Building2,
  Calendar,
  Send,
  BarChart3,
  FileCheck,
  FileX,
  Search
} from "lucide-react";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "../components/ui/hover-card";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper to check if it's a single-day view
const isSingleDayView = (daysValue) => daysValue === "1" || daysValue === "yesterday";

// Color helper for completion rates
const rateBg = (rate) => {
  if (rate >= 90) return "bg-green-100 text-green-700";
  if (rate >= 70) return "bg-yellow-100 text-yellow-700";
  return "bg-red-100 text-red-700";
};

// Mini sparkline SVG component
function Sparkline({ data = [], width = 80, height = 24 }) {
  if (!data || data.length < 2) return null;
  const max = Math.max(...data, 100);
  const min = Math.min(...data, 0);
  const range = max - min || 1;
  const step = width / (data.length - 1);
  const points = data.map((v, i) => `${i * step},${height - ((v - min) / range) * (height - 4) - 2}`);
  const lastVal = data[data.length - 1];
  const color = lastVal >= 90 ? "#16a34a" : lastVal >= 70 ? "#ca8a04" : "#dc2626";
  return (
    <svg width={width} height={height} className="inline-block">
      <polyline fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" points={points.join(" ")} />
      <circle cx={(data.length - 1) * step} cy={height - ((lastVal - min) / range) * (height - 4) - 2} r="2" fill={color} />
    </svg>
  );
}

export default function DashboardPage() {
  const { isAdmin } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState("7");
  const [sendingReminders, setSendingReminders] = useState({});
  const [reportsDialog, setReportsDialog] = useState({ open: false, manager: null, reports: [] });
  const [managerSearch, setManagerSearch] = useState('');

  const fetchStats = async (daysParam) => {
    try {
      setLoading(true);
      let url = `${API}/dashboard/stats?days=1`;
      
      if (daysParam === "yesterday") {
        // Use backend to calculate last business day (skips weekends)
        url = `${API}/dashboard/stats?days=1&use_last_business_day=true`;
      } else if (daysParam === "1") {
        // Today - just days=1
        url = `${API}/dashboard/stats?days=1`;
      } else {
        // Multi-day views
        url = `${API}/dashboard/stats?days=${daysParam}`;
      }
      
      const response = await axios.get(url);
      setStats(response.data);
    } catch (error) {
      toast.error("Failed to load dashboard stats");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats(days);
  }, [days]);

  const sendTeamReminders = async (teamId, missingUsers) => {
    if (!missingUsers || missingUsers.length === 0) return;
    
    setSendingReminders(prev => ({ ...prev, [teamId]: true }));
    try {
      const userIds = missingUsers.map(u => u.id);
      await axios.post(`${API}/admin/send-reminders`, {
        log_date: stats.summary.date,
        user_ids: userIds
      });
      toast.success(`Reminders sent to ${missingUsers.length} users`);
    } catch (error) {
      toast.error("Failed to send reminders");
    } finally {
      setSendingReminders(prev => ({ ...prev, [teamId]: false }));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="spinner" />
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-500">Failed to load dashboard data</p>
        <Button onClick={fetchStats} className="mt-4">Retry</Button>
      </div>
    );
  }

  const { summary, team_stats, manager_stats, daily_trend, users_with_managers = [] } = stats;

  // Calculate max for chart scaling
  const maxSubmissions = Math.max(...daily_trend.map(d => d.total), 1);

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            Dashboard
          </h1>
          <p className="text-slate-500 mt-1">Managers performance overview</p>
        </div>
        
        <div className="flex items-center gap-3">
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger className="w-[160px]" data-testid="days-filter">
              <Calendar className="h-4 w-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1">Today</SelectItem>
              <SelectItem value="yesterday">Yesterday</SelectItem>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="14">Last 14 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Total Teams</p>
                <p className="text-3xl font-bold mt-1">{summary.total_teams}</p>
              </div>
              <div className="h-12 w-12 bg-indigo-100 rounded-full flex items-center justify-center">
                <Building2 className="h-6 w-6 text-indigo-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Total Managers</p>
                <p className="text-3xl font-bold mt-1">{summary.total_managers || 0}</p>
              </div>
              <div className="h-12 w-12 bg-purple-100 rounded-full flex items-center justify-center">
                <Users className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Total Members</p>
                <p className="text-3xl font-bold mt-1">{summary.total_members}</p>
              </div>
              <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center">
                <Users className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">{days === "yesterday" ? "Yesterday" : "Today"} Submitted</p>
                <p className="text-3xl font-bold mt-1 text-green-600">{summary.today_submitted}</p>
              </div>
              <div className="h-12 w-12 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle2 className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">{days === "yesterday" ? "Yesterday" : "Today"} Missing</p>
                {summary.today_missing > 0 ? (
                  <HoverCard>
                    <HoverCardTrigger asChild>
                      <p className="text-3xl font-bold mt-1 text-red-600 cursor-pointer hover:underline">
                        {summary.today_missing}
                      </p>
                    </HoverCardTrigger>
                    <HoverCardContent className="w-72">
                      <div className="space-y-2">
                        <h4 className="font-semibold text-sm">Missing Users ({summary.today_missing})</h4>
                        <div className="max-h-48 overflow-y-auto space-y-2">
                          {summary.missing_users && summary.missing_users.map(user => (
                            <div key={user.id} className="text-sm border-b border-slate-100 pb-2 last:border-0">
                              <p className="font-medium">{user.full_name}</p>
                              <p className="text-slate-500 text-xs">{user.email}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    </HoverCardContent>
                  </HoverCard>
                ) : (
                  <p className="text-3xl font-bold mt-1 text-red-600">{summary.today_missing}</p>
                )}
              </div>
              <div className="h-12 w-12 bg-red-100 rounded-full flex items-center justify-center">
                <XCircle className="h-6 w-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Period Stats - Show when not single-day view (Today or Yesterday) */}
      {!isSingleDayView(days) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <Card className="bg-gradient-to-br from-emerald-50 to-white border-emerald-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-emerald-700">Period Total Submitted</p>
                  <p className="text-3xl font-bold mt-1 text-emerald-600">{summary.period_total_submitted || 0}</p>
                  <p className="text-xs text-emerald-600 mt-1">of {summary.period_total_expected || 0} expected ({summary.period_submission_rate || 0}%)</p>
                </div>
                <div className="h-12 w-12 bg-emerald-100 rounded-full flex items-center justify-center">
                  <FileCheck className="h-6 w-6 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-red-50 to-white border-red-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-red-700">Period Total Missed</p>
                  <p className="text-3xl font-bold mt-1 text-red-600">{summary.period_total_missed || 0}</p>
                  <p className="text-xs text-red-600 mt-1">{summary.period_total_expected > 0 ? (100 - (summary.period_submission_rate || 0)).toFixed(1) : 0}% missed rate</p>
                </div>
                <div className="h-12 w-12 bg-red-100 rounded-full flex items-center justify-center">
                  <FileX className="h-6 w-6 text-red-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-blue-50 to-white border-blue-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-blue-700">Period Submission Rate</p>
                  <p className="text-3xl font-bold mt-1 text-blue-600">{summary.period_submission_rate || 0}%</p>
                  <p className="text-xs text-blue-600 mt-1">Last {days} days (weekdays only)</p>
                </div>
                <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <TrendingUp className="h-6 w-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Overall Completion Rate */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            {days === "yesterday" ? "Yesterday's" : "Today's"} Completion Rate
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <div className="flex-1 bg-slate-100 rounded-full h-4 overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-indigo-500 to-indigo-600 rounded-full transition-all duration-500"
                style={{ width: `${summary.overall_rate}%` }}
              />
            </div>
            <span className="text-2xl font-bold text-indigo-600 min-w-[80px] text-right">
              {summary.overall_rate}%
            </span>
          </div>
          <p className="text-sm text-slate-500 mt-2">
            {summary.today_submitted} of {summary.total_members} members have submitted {days === "yesterday" ? "yesterday" : "today"}
          </p>
        </CardContent>
      </Card>

      {/* Submission Trend Chart - Hide for single-day views */}
      {!isSingleDayView(days) && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Submission Trend ({days} days)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-end gap-1 h-32 mt-4">
              {daily_trend.map((day, index) => (
                <div 
                  key={day.date} 
                  className="flex-1 flex flex-col items-center gap-1"
                >
                  <div className="w-full flex flex-col items-center justify-end h-24">
                    <span className="text-xs text-slate-500 mb-1">{day.submitted}</span>
                    <div 
                      className={`w-full rounded-t transition-all duration-300 ${
                        day.is_weekend ? 'bg-slate-200' : 'bg-indigo-500'
                      }`}
                      style={{ 
                        height: `${Math.max((day.submitted / maxSubmissions) * 100, 4)}%`,
                        minHeight: '4px'
                      }}
                    />
                  </div>
                  <span className={`text-xs ${day.is_weekend ? 'text-slate-400' : 'text-slate-600'}`}>
                    {day.day}
                  </span>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-center gap-4 mt-4 text-xs text-slate-500">
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 bg-indigo-500 rounded" />
                <span>Weekday</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 bg-slate-200 rounded" />
                <span>Weekend</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Managers Performance Table */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="h-5 w-5" />
              Managers Performance
            </CardTitle>
            {manager_stats && manager_stats.length > 0 && (
              <div className="relative w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search manager or user..."
                  value={managerSearch}
                  onChange={(e) => setManagerSearch(e.target.value)}
                  className="pl-9 h-8 text-sm"
                />
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {(!manager_stats || manager_stats.length === 0) ? (
            <div className="text-center py-8 text-slate-500">
              No managers found. Assign managers via the "Reports To" field on user profiles.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-2 text-sm font-medium text-slate-500">Manager</th>
                    <th className="text-center py-3 px-2 text-sm font-medium text-slate-500">Direct Reports</th>
                    <th className="text-center py-3 px-2 text-sm font-medium text-slate-500">{isSingleDayView(days) ? (days === "yesterday" ? "Yesterday" : "Today") : "Submitted"}</th>
                    <th className="text-center py-3 px-2 text-sm font-medium text-slate-500">Missing</th>
                    <th className="text-center py-3 px-2 text-sm font-medium text-slate-500">Rate</th>
                    {!isSingleDayView(days) && <th className="text-center py-3 px-2 text-sm font-medium text-slate-500">Trend</th>}
                    {!isSingleDayView(days) && <th className="text-center py-3 px-2 text-sm font-medium text-slate-500">{days}d Total</th>}
                    <th className="text-center py-3 px-2 text-sm font-medium text-slate-500">{isSingleDayView(days) ? "Rate" : `${days}d Avg`}</th>
                    {isAdmin && <th className="text-center py-3 px-2 text-sm font-medium text-slate-500">Action</th>}
                  </tr>
                </thead>
                <tbody>
                  {manager_stats.filter(mgr => {
                    if (!managerSearch.trim()) return true;
                    const q = managerSearch.toLowerCase();
                    if (mgr.manager_name.toLowerCase().includes(q)) return true;
                    const directReports = users_with_managers.filter(u => u.manager_id === mgr.manager_id);
                    if (directReports.some(r => r.full_name.toLowerCase().includes(q) || r.email.toLowerCase().includes(q))) return true;
                    return false;
                  }).sort((a, b) => a.manager_name.localeCompare(b.manager_name)).map((mgr) => (
                    <tr key={mgr.manager_id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-3 px-2">
                        <span className="font-medium">{mgr.manager_name}</span>
                        {managerSearch.trim() && (() => {
                          const q = managerSearch.toLowerCase();
                          if (mgr.manager_name.toLowerCase().includes(q)) return null;
                          const matched = users_with_managers.filter(u => u.manager_id === mgr.manager_id && (u.full_name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q)));
                          if (matched.length === 0) return null;
                          return (
                            <div className="text-xs text-blue-600 mt-0.5">
                              Manager of: {matched.map(r => r.full_name).join(', ')}
                            </div>
                          );
                        })()}
                      </td>
                      <td className="py-3 px-2 text-center">
                        <button
                          className="text-blue-600 font-medium hover:underline cursor-pointer"
                          onClick={() => setReportsDialog({ open: true, manager: mgr.manager_name, reports: users_with_managers.filter(u => u.manager_id === mgr.manager_id) })}
                        >
                          {mgr.member_count}
                        </button>
                      </td>
                      <td className="py-3 px-2 text-center">
                        <span className="text-green-600 font-medium">
                          {isSingleDayView(days) ? mgr.today_submitted : mgr.period_submitted}
                        </span>
                      </td>
                      <td className="py-3 px-2 text-center">
                        {(isSingleDayView(days) ? mgr.today_missing : (mgr.period_total - mgr.period_submitted)) > 0 ? (
                          isSingleDayView(days) ? (
                            <HoverCard>
                              <HoverCardTrigger asChild>
                                <button className="text-red-600 font-medium hover:underline cursor-pointer">
                                  {mgr.today_missing}
                                </button>
                              </HoverCardTrigger>
                              <HoverCardContent className="w-64">
                                <div className="space-y-2">
                                  <h4 className="font-semibold text-sm">Missing Users</h4>
                                  <div className="max-h-40 overflow-y-auto space-y-1">
                                    {mgr.missing_users.map(user => (
                                      <div key={user.id} className="text-sm">
                                        <p className="font-medium">{user.full_name}</p>
                                        <p className="text-slate-500 text-xs">{user.email}</p>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </HoverCardContent>
                            </HoverCard>
                          ) : (
                            <span className="text-red-600 font-medium">{mgr.period_total - mgr.period_submitted}</span>
                          )
                        ) : (
                          <span className="text-slate-400">0</span>
                        )}
                      </td>
                      <td className="py-3 px-2 text-center">
                        {(() => {
                          const rate = isSingleDayView(days) ? mgr.completion_rate : mgr.period_rate;
                          return (
                            <div className="flex items-center justify-center gap-2">
                              <div className="w-16 bg-slate-100 rounded-full h-2 overflow-hidden">
                                <div
                                  className={`h-full rounded-full ${rate >= 90 ? 'bg-green-500' : rate >= 70 ? 'bg-yellow-500' : 'bg-red-500'}`}
                                  style={{ width: `${rate}%` }}
                                />
                              </div>
                              <span className={`text-sm font-semibold px-1.5 py-0.5 rounded ${rateBg(rate)}`}>{rate}%</span>
                            </div>
                          );
                        })()}
                      </td>
                      {!isSingleDayView(days) && (
                        <td className="py-3 px-2 text-center">
                          <Sparkline data={mgr.sparkline || []} />
                        </td>
                      )}
                      {!isSingleDayView(days) && (
                        <td className="py-3 px-2 text-center">
                          <span className="text-sm font-medium text-emerald-600">{mgr.period_submitted}</span>
                          <span className="text-xs text-slate-400">/{mgr.period_total}</span>
                        </td>
                      )}
                      <td className="py-3 px-2 text-center">
                        <span className={`text-sm font-semibold px-1.5 py-0.5 rounded ${rateBg(mgr.period_rate)}`}>{mgr.period_rate}%</span>
                      </td>
                      {isAdmin && (
                        <td className="py-3 px-2 text-center">
                          {mgr.today_missing > 0 && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => sendTeamReminders(mgr.manager_id, mgr.missing_users)}
                              disabled={sendingReminders[mgr.manager_id]}
                              data-testid={`send-reminder-${mgr.manager_id}`}
                            >
                              {sendingReminders[mgr.manager_id] ? (
                                <span className="spinner-sm" />
                              ) : (
                                <Send className="h-3 w-3" />
                              )}
                            </Button>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
      {/* Direct Reports Dialog */}
      <Dialog open={reportsDialog.open} onOpenChange={(open) => setReportsDialog(prev => ({ ...prev, open }))}>
        <DialogContent className="max-w-lg">
          <DialogHeader className="pb-3 border-b">
            <DialogTitle className="flex items-center gap-2 text-lg">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Users className="h-5 w-5 text-blue-600" />
              </div>
              {reportsDialog.manager}'s Direct Reports
              <Badge className="ml-auto bg-blue-100 text-blue-700 hover:bg-blue-100">{reportsDialog.reports.length}</Badge>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-1.5" style={{ maxHeight: reportsDialog.reports.length > 15 ? `${15 * 52}px` : 'none', overflowY: reportsDialog.reports.length > 15 ? 'auto' : 'visible' }}>
            {[...reportsDialog.reports].sort((a, b) => (a.full_name || '').localeCompare(b.full_name || '')).map((user, idx) => (
              <div key={user.id} className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-50 transition-colors border border-slate-100">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-br from-blue-100 to-indigo-100 text-blue-700 text-xs font-bold shrink-0">
                  {(user.full_name || '?').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{user.full_name}</p>
                  <p className="text-xs text-slate-500 truncate">{user.email}</p>
                </div>
                <span className="text-xs text-slate-400 font-medium shrink-0">{idx + 1}</span>
              </div>
            ))}
            {reportsDialog.reports.length === 0 && (
              <p className="text-sm text-slate-500 text-center py-8">No direct reports</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
